import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import ExtensionIcon from "@mui/icons-material/Extension";
import SyncIcon from "@mui/icons-material/Sync";
import JsonSchemaForm from "./JsonSchemaForm";
import Loading from "./Loading";
import BKGSourcePicker from "./Modals/Integrations/BKGSourcePicker";
import {
  AvailableIntegration,
  WorkspaceIntegrationSelection,
  WorkspaceIntegrationSelectionCreate,
  WorkspaceIntegrationSelectionUpdate,
} from "../common/types";
import {
  useCreateWorkspaceIntegrationSelection,
  useGetDeltekOAuthAuthorizationUrl,
  useDeleteWorkspaceIntegrationSelection,
  useListAvailableIntegrations,
  useListWorkspaceIntegrationSelections,
  usePreviewSharePointDisconnect,
  useRunSharePointSync,
  useTriggerIntegrationSync,
  useUpdateWorkspaceIntegrationSelection,
} from "../services/api";
import { useIntegrationCatalog } from "../services/integrationCatalog";
import {
  buildWorkspaceIntegrationRows,
  hasWorkspaceConfiguration,
  isBKGIntegrationKey,
  needsNoWorkspaceConfiguration,
  supportsSync,
  WorkspaceIntegrationRow,
} from "./workspaceIntegrationSelectionsUtils";

interface WorkspaceIntegrationSelectionsProps {
  workspaceId: string;
  organizationId?: string | null;
  enabledModules?: string[];
  // Called after a selection is enabled, changed or removed, so the caller can
  // refresh anything derived from the workspace's enabled integrations.
  onSelectionsChanged?: () => void | Promise<void>;
  emailEnabled?: boolean;
}

const WorkspaceIntegrationSelections: React.FC<
  WorkspaceIntegrationSelectionsProps
> = ({
  workspaceId,
  organizationId,
  enabledModules = [],
  emailEnabled,
  onSelectionsChanged,
}) => {
  const [availableIntegrations, setAvailableIntegrations] = useState<
    AvailableIntegration[]
  >([]);
  const [selections, setSelections] = useState<WorkspaceIntegrationSelection[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeRow, setActiveRow] = useState<WorkspaceIntegrationRow | null>(
    null,
  );
  const [bkgPickerRow, setBkgPickerRow] =
    useState<WorkspaceIntegrationRow | null>(null);
  const [formValue, setFormValue] = useState<Record<string, any>>({});
  const [modalError, setModalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [enablingId, setEnablingId] = useState<string | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [sharePointDisconnectRow, setSharePointDisconnectRow] =
    useState<WorkspaceIntegrationRow | null>(null);
  const [sharePointDocumentCount, setSharePointDocumentCount] = useState<
    number | null
  >(null);
  const [
    loadingSharePointDisconnectPreview,
    setLoadingSharePointDisconnectPreview,
  ] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [provisionalSelectionId, setProvisionalSelectionId] = useState<
    string | null
  >(null);
  const handledDeltekOAuthResult = useRef<string | null>(null);
  const skipNextLoadRows = useRef(false);

  const listAvailableIntegrations = useListAvailableIntegrations();
  const listSelections = useListWorkspaceIntegrationSelections();
  const createSelection = useCreateWorkspaceIntegrationSelection();
  const updateSelection = useUpdateWorkspaceIntegrationSelection();
  const deleteSelection = useDeleteWorkspaceIntegrationSelection();
  const triggerSync = useTriggerIntegrationSync();
  const runSharePointSync = useRunSharePointSync();
  const previewSharePointDisconnect = usePreviewSharePointDisconnect();
  const getDeltekAuthorizationUrl = useGetDeltekOAuthAuthorizationUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    entries: catalogEntries,
    loading: catalogLoading,
    error: catalogError,
    refresh: refreshCatalog,
  } = useIntegrationCatalog(undefined, workspaceId);

  const loadRows = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      setError(null);
      const [availableRows, selectionRows] = await Promise.all([
        listAvailableIntegrations(workspaceId),
        listSelections(workspaceId),
      ]);
      setAvailableIntegrations(availableRows);
      setSelections(selectionRows);
    } catch (requestError: any) {
      setError(
        requestError?.message ||
          "Failed to load workspace integrations. Refresh and try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [listAvailableIntegrations, listSelections, workspaceId]);

  useEffect(() => {
    if (skipNextLoadRows.current) {
      skipNextLoadRows.current = false;
      return;
    }
    if (new URLSearchParams(location.search).has("deltek_oauth")) return;
    loadRows();
  }, [loadRows, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get("deltek_oauth");
    if (!result) {
      handledDeltekOAuthResult.current = null;
      return;
    }
    if (handledDeltekOAuthResult.current === location.search) return;
    handledDeltekOAuthResult.current = location.search;

    const finishOAuth = async () => {
      let callbackError: string | null = null;
      if (result === "connected") {
        setSuccessMessage("Deltek account connected to this workspace.");
        setError(null);
      } else {
        callbackError = "Deltek connection was cancelled or failed. Try again.";
      }

      await Promise.all([loadRows(), refreshCatalog()]);
      if (callbackError) setError(callbackError);
      skipNextLoadRows.current = true;
      params.delete("deltek_oauth");
      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
          hash: location.hash,
        },
        { replace: true },
      );
    };
    void finishOAuth();
  }, [
    createSelection,
    listAvailableIntegrations,
    listSelections,
    loadRows,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    refreshCatalog,
    updateSelection,
    workspaceId,
  ]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = window.setTimeout(() => setSuccessMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [successMessage]);

  const rows = useMemo(
    () =>
      buildWorkspaceIntegrationRows(
        availableIntegrations,
        catalogEntries,
        selections,
      ),
    [availableIntegrations, catalogEntries, selections],
  );

  const syncErrorNames = useMemo(
    () =>
      selections
        .filter((s) => s.sync_status === "error" && s.sync_error_message)
        .map((s) => s.integration_name || "Integration"),
    [selections],
  );

  const openConfigureModal = (row: WorkspaceIntegrationRow) => {
    if (isBKGIntegrationKey(row.catalog.key)) {
      setBkgPickerRow(row);
      return;
    }

    setActiveRow(row);
    setModalError(null);
    setProvisionalSelectionId(null);
    setFormValue(row.selection?.workspace_config || {});
  };

  const closeConfigureModal = async () => {
    const pendingSelectionId = provisionalSelectionId;
    const pendingRow = activeRow;
    const pendingFormValue = formValue;

    setActiveRow(null);
    setFormValue({});
    setModalError(null);
    setProvisionalSelectionId(null);

    if (
      pendingSelectionId &&
      pendingRow?.catalog.key === "sharepoint" &&
      !hasWorkspaceConfiguration({
        ...pendingRow,
        selection: {
          ...(pendingRow.selection || {
            id: pendingSelectionId,
            workspace_id: workspaceId,
            organization_integration_id: pendingRow.available.id,
            workspace_config: pendingFormValue,
            enabled: true,
            sync_status: "pending",
            created_at: "",
            permission_verification_enabled: false,
          }),
          workspace_config: pendingFormValue,
          enabled: true,
        } as WorkspaceIntegrationSelection,
      })
    ) {
      try {
        await deleteSelection(workspaceId, pendingSelectionId);
        await loadRows();
      } catch {
        // Leave cleanup failures silent; the real action is still available on next open.
      }
    }
  };

  const notifySelectionsChanged = async () => {
    try {
      await onSelectionsChanged?.();
    } catch {
      // The selection itself was saved; a failed refresh must not report it as failed.
    }
  };

  // For integrations with nothing to configure at workspace level, enabling is
  // one click rather than an empty form with a Save button.
  const handleEnable = async (row: WorkspaceIntegrationRow) => {
    try {
      setEnablingId(row.available.id);
      setError(null);
      if (row.selection) {
        await updateSelection(workspaceId, row.selection.id, {
          workspace_config: row.selection.workspace_config || {},
          enabled: true,
        });
      } else {
        await createSelection(workspaceId, {
          organization_integration_id: row.available.id,
          workspace_config: {},
          enabled: true,
        });
      }
      setSuccessMessage(
        `${row.catalog.display_name || row.available.name} enabled for this workspace.`,
      );
      await loadRows();
      await notifySelectionsChanged();
    } catch (enableError: any) {
      setError(
        enableError?.message || "Failed to enable the workspace integration.",
      );
    } finally {
      setEnablingId(null);
    }
  };

  const handleSave = async () => {
    if (!activeRow) return;

    setSaving(true);
    setModalError(null);
    try {
      const widgetName = activeRow.catalog.workspace_schema?.["x-widget"];
      if (activeRow.selection) {
        const payload: WorkspaceIntegrationSelectionUpdate = {
          workspace_config: formValue,
          enabled: true,
        };
        await updateSelection(workspaceId, activeRow.selection.id, payload);
      } else {
        const payload: WorkspaceIntegrationSelectionCreate = {
          organization_integration_id: activeRow.available.id,
          workspace_config: formValue,
          enabled: true,
        };
        await createSelection(workspaceId, payload);
      }

      setSuccessMessage(
        widgetName === "email_senders_and_templates"
          ? "Email integration enabled for this workspace."
          : "Integration configuration saved.",
      );
      await loadRows();
      await notifySelectionsChanged();
      await closeConfigureModal();
    } catch (saveError: any) {
      setModalError(
        saveError?.message ||
          "Failed to save workspace integration configuration.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeltekConnect = async (row: WorkspaceIntegrationRow) => {
    setError(null);
    setConnectingId(row.available.id);
    try {
      const hasValidUserToken =
        row.catalog.user_connection_status === "connected";
      if (hasValidUserToken && !row.selection?.enabled) {
        if (!row.selection) {
          await createSelection(workspaceId, {
            organization_integration_id: row.available.id,
            workspace_config: {},
            enabled: true,
          });
        } else {
          await updateSelection(workspaceId, row.selection.id, {
            enabled: true,
          });
        }
        setSuccessMessage("Deltek enabled for this workspace.");
        await loadRows();
        return;
      }

      // OAuth start and callback both require an enabled workspace selection.
      // If OAuth is cancelled or fails, the missing/invalid user token keeps
      // the card in the Connect state and the runtime cannot use this selection.
      if (!hasValidUserToken && !row.selection?.enabled) {
        if (row.selection) {
          await updateSelection(workspaceId, row.selection.id, {
            enabled: true,
          });
        } else {
          await createSelection(workspaceId, {
            organization_integration_id: row.available.id,
            workspace_config: {},
            enabled: true,
          });
        }
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const response = await getDeltekAuthorizationUrl(
        workspaceId,
        row.available.id,
        returnTo,
      );
      window.location.assign(response.authorization_url);
    } catch (connectError: any) {
      setError(
        connectError?.message || "Failed to start Deltek account connection.",
      );
    } finally {
      setConnectingId(null);
    }
  };

  const ensureSharePointSelection = useCallback(async () => {
    if (!activeRow || activeRow.catalog.key !== "sharepoint") {
      return activeRow?.selection || null;
    }
    if (activeRow.selection) {
      return activeRow.selection;
    }

    const createdSelection = await createSelection(workspaceId, {
      organization_integration_id: activeRow.available.id,
      workspace_config: {},
      enabled: true,
    });
    setProvisionalSelectionId(createdSelection.id);
    setActiveRow((currentRow) =>
      currentRow
        ? {
            ...currentRow,
            selection: createdSelection,
          }
        : currentRow,
    );
    setSelections((currentSelections) => [
      ...currentSelections,
      createdSelection,
    ]);
    return createdSelection;
  }, [activeRow, createSelection, workspaceId]);

  const handleDisconnect = async (row: WorkspaceIntegrationRow) => {
    if (!row.selection) return;

    try {
      setDisconnectingId(row.selection.id);
      await deleteSelection(workspaceId, row.selection.id);
      if (row.catalog.key === "sharepoint") {
        // The backend removes SharePoint documents only when a sync observes no
        // active selection. Trigger that cleanup here rather than waiting for
        // the Documents view to mount.
        await runSharePointSync(workspaceId);
      }
      setSuccessMessage(
        needsNoWorkspaceConfiguration(row)
          ? `${row.catalog.display_name || row.available.name} disabled for this workspace.`
          : "Integration disconnected from this workspace.",
      );
      await loadRows();
      await notifySelectionsChanged();
    } catch (deleteError: any) {
      setError(
        deleteError?.message ||
          "Failed to disconnect the workspace integration.",
      );
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleDisconnectClick = async (row: WorkspaceIntegrationRow) => {
    if (row.catalog.key !== "sharepoint") {
      await handleDisconnect(row);
      return;
    }

    try {
      setLoadingSharePointDisconnectPreview(true);
      const preview = await previewSharePointDisconnect(workspaceId);
      setSharePointDocumentCount(preview.document_count);
      setSharePointDisconnectRow(row);
    } catch (previewError: any) {
      setError(
        previewError?.message ||
          "Failed to determine which SharePoint index entries would be removed.",
      );
    } finally {
      setLoadingSharePointDisconnectPreview(false);
    }
  };

  const confirmSharePointDisconnect = async () => {
    if (!sharePointDisconnectRow) return;
    const row = sharePointDisconnectRow;
    setSharePointDisconnectRow(null);
    setSharePointDocumentCount(null);
    await handleDisconnect(row);
  };

  const handleBKGSelectionChanged = async (
    nextSelection: WorkspaceIntegrationSelection | null,
  ) => {
    if (!bkgPickerRow) return;

    setSelections((currentSelections) => {
      if (!nextSelection) {
        return currentSelections.filter(
          (selection) => selection.id !== bkgPickerRow.selection?.id,
        );
      }

      const existingIndex = currentSelections.findIndex(
        (selection) => selection.id === nextSelection.id,
      );
      if (existingIndex === -1) {
        return [...currentSelections, nextSelection];
      }

      return currentSelections.map((selection) =>
        selection.id === nextSelection.id ? nextSelection : selection,
      );
    });
    setBkgPickerRow((currentRow) =>
      currentRow
        ? {
            ...currentRow,
            selection: nextSelection || undefined,
          }
        : currentRow,
    );
    setSuccessMessage(
      nextSelection
        ? "BKG source workspace saved."
        : "BKG source workspace removed.",
    );
    await loadRows();
  };

  const handleSyncNow = async (row: WorkspaceIntegrationRow) => {
    if (!row.selection) return;

    try {
      setSyncingId(row.selection.id);
      if (row.catalog.key === "sharepoint") {
        // Run SharePoint sync synchronously so we get immediate pass/fail feedback.
        // The backend persists errors to the selection row, so loadRows() below
        // will populate the syncErrorMessages warning banner automatically.
        const result = await runSharePointSync(workspaceId);
        if (!result.errors?.length && !result.skipped) {
          setSuccessMessage(
            `Sync complete — ${result.new} new, ${result.updated} updated, ${result.deleted} deleted.`,
          );
        }
      } else {
        await triggerSync(workspaceId, row.selection.id);
        setSuccessMessage("Sync triggered.");
      }
      await loadRows();
    } catch (syncError: any) {
      setError(syncError?.message || "Failed to trigger sync.");
    } finally {
      setSyncingId(null);
    }
  };

  const isLoading = loading || catalogLoading;
  const widgetName = activeRow?.catalog.workspace_schema?.["x-widget"];
  const isEmailWidget = widgetName === "email_senders_and_templates";

  return (
    <Stack spacing={2.5}>
      {successMessage && (
        <Alert color="success" variant="soft">
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert color="danger" variant="soft">
          {error}
        </Alert>
      )}
      {catalogError && (
        <Alert color="danger" variant="soft">
          {catalogError}
        </Alert>
      )}
      {syncErrorNames.map((name) => (
        <Alert key={name} color="warning" variant="soft">
          {name} sync failed. Please contact your support team.
        </Alert>
      ))}

      {isLoading ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Loading />
        </Box>
      ) : rows.length === 0 ? (
        <Alert color="neutral" variant="soft">
          No workspace-configurable integrations are available yet. Organization
          admins need to configure them first.
        </Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {rows.map((row) => {
            const isDeltek = row.catalog.key === "deltek";
            const deltekConnected =
              isDeltek &&
              row.selection?.enabled === true &&
              row.catalog.user_connection_status === "connected";
            const configured = isDeltek
              ? deltekConnected
              : hasWorkspaceConfiguration(row);
            const noConfig = needsNoWorkspaceConfiguration(row);
            const configureDisabled =
              row.catalog.key === "email" && emailEnabled === false;

            return (
              <Card key={row.available.id} variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={2}
                    >
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ExtensionIcon color="primary" />
                          <Typography level="title-lg">
                            {row.catalog.display_name || row.available.name}
                          </Typography>
                        </Stack>
                        {row.catalog.description && (
                          <Typography level="body-sm" color="neutral">
                            {row.catalog.description}
                          </Typography>
                        )}
                      </Stack>
                      <Chip
                        size="sm"
                        color={configured ? "success" : "neutral"}
                        variant="soft"
                      >
                        {isDeltek
                          ? configured
                            ? `Connected${
                                row.catalog.user_connection_label
                                  ? ` as ${row.catalog.user_connection_label}`
                                  : ""
                              }`
                            : row.selection?.enabled === true &&
                                row.catalog.user_connection_status ===
                                  "reconnect"
                              ? "Reconnect required"
                              : "Not connected"
                          : noConfig
                            ? configured
                              ? "Enabled"
                              : "Not enabled"
                            : configured
                              ? "Configured"
                              : "Available"}
                      </Chip>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip size="sm" variant="outlined" color="neutral">
                        {row.catalog.category || "Uncategorized"}
                      </Chip>
                      {row.catalog.key === "email" &&
                        emailEnabled === false && (
                          <Chip size="sm" variant="soft" color="warning">
                            Organization email disabled
                          </Chip>
                        )}
                      {row.selection?.sync_status === "error" && (
                        <Chip size="sm" variant="soft" color="danger">
                          Sync failed
                        </Chip>
                      )}
                    </Stack>

                    {row.catalog.key === "email" && emailEnabled === false && (
                      <Alert color="warning" variant="soft" size="sm">
                        Email is disabled at the organization level. Manage it
                        in{" "}
                        <Link href="/organization/settings">
                          Organization Settings
                        </Link>
                        .
                      </Alert>
                    )}

                    {row.selection?.sync_status === "error" && (
                      <Alert color="danger" variant="soft" size="sm">
                        Sync failed. Please contact your support team.
                      </Alert>
                    )}

                    {isBKGIntegrationKey(row.catalog.key) &&
                      (row.selection?.source_workspace_id ||
                        row.selection?.workspace_config
                          ?.source_workspace_id) && (
                        <Alert color="primary" variant="soft" size="sm">
                          BKG source:{" "}
                          {row.selection.source_workspace_name ||
                            row.selection.workspace_config
                              ?.source_workspace_name ||
                            row.selection.source_workspace_id ||
                            row.selection.workspace_config?.source_workspace_id}
                        </Alert>
                      )}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                    >
                      {isDeltek ? (
                        <Button
                          variant="solid"
                          color="primary"
                          loading={connectingId === row.available.id}
                          onClick={() => void handleDeltekConnect(row)}
                        >
                          {deltekConnected
                            ? "Reconnect Deltek"
                            : "Connect Deltek"}
                        </Button>
                      ) : noConfig ? (
                        configured ? (
                          <Box />
                        ) : (
                          <Button
                            variant="solid"
                            color="primary"
                            loading={enablingId === row.available.id}
                            onClick={() => void handleEnable(row)}
                          >
                            Enable
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="solid"
                          color="primary"
                          startDecorator={
                            row.catalog.key === "email" ? (
                              <EmailIcon />
                            ) : (
                              <EditIcon />
                            )
                          }
                          disabled={configureDisabled}
                          onClick={() => openConfigureModal(row)}
                        >
                          {isBKGIntegrationKey(row.catalog.key) && configured
                            ? "Change"
                            : "Configure"}
                        </Button>
                      )}

                      <Stack direction="row" spacing={1}>
                        {row.selection && supportsSync(row) && (
                          <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<SyncIcon />}
                            loading={syncingId === row.selection.id}
                            onClick={() => handleSyncNow(row)}
                          >
                            Sync now
                          </Button>
                        )}
                        {row.selection && (
                          <Button
                            size="sm"
                            variant="outlined"
                            color="danger"
                            startDecorator={<DeleteIcon />}
                            loading={
                              disconnectingId === row.selection.id ||
                              (row.catalog.key === "sharepoint" &&
                                loadingSharePointDisconnectPreview)
                            }
                            onClick={() => handleDisconnectClick(row)}
                          >
                            {isBKGIntegrationKey(row.catalog.key)
                              ? "Remove"
                              : noConfig
                                ? "Disable"
                                : "Disconnect"}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Modal open={Boolean(activeRow)} onClose={closeConfigureModal}>
        <ModalDialog sx={{ width: "min(860px, calc(100vw - 32px))" }}>
          <DialogTitle>
            Configure{" "}
            {activeRow?.catalog.display_name || activeRow?.available.name}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              {modalError && (
                <Alert color="danger" variant="soft">
                  {modalError}
                </Alert>
              )}
              {activeRow?.catalog.workspace_schema ? (
                <JsonSchemaForm
                  schema={activeRow.catalog.workspace_schema}
                  value={formValue}
                  onChange={setFormValue}
                  disabled={saving}
                  idPrefix={`workspace-integration-${activeRow.catalog.key}`}
                  widgetContext={{
                    workspaceId,
                    organizationId,
                    enabledModules,
                    emailEnabled,
                    availableIntegration: activeRow.available,
                    selection: activeRow.selection,
                    ensureSelection: ensureSharePointSelection,
                  }}
                />
              ) : (
                <Alert color="neutral" variant="soft">
                  No workspace configuration fields are required.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSave}
              loading={saving}
            >
              {isEmailWidget ? "Done" : "Save"}
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={closeConfigureModal}
              disabled={saving}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal
        open={Boolean(sharePointDisconnectRow)}
        onClose={() => {
          setSharePointDisconnectRow(null);
          setSharePointDocumentCount(null);
        }}
      >
        <ModalDialog>
          <DialogTitle>Disconnect SharePoint?</DialogTitle>
          <DialogContent>
            Remove index entries and stored copies for{" "}
            {sharePointDocumentCount ?? 0} document
            {sharePointDocumentCount === 1 ? "" : "s"}; the files stay in
            SharePoint.
          </DialogContent>
          <DialogActions>
            <Button color="danger" onClick={confirmSharePointDisconnect}>
              Disconnect and remove index entries
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => {
                setSharePointDisconnectRow(null);
                setSharePointDocumentCount(null);
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {bkgPickerRow && (
        <BKGSourcePicker
          open={Boolean(bkgPickerRow)}
          workspaceId={workspaceId}
          organizationIntegrationId={bkgPickerRow.available.id}
          selection={bkgPickerRow.selection || null}
          onClose={() => setBkgPickerRow(null)}
          onSelectionChanged={handleBKGSelectionChanged}
        />
      )}
    </Stack>
  );
};

export default WorkspaceIntegrationSelections;
